import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card } from "@/components/ui/card";
import { ParamsPanel, validar } from "./ParamsPanel";
import { fmtMXN, fmtPct, generarTabla, SISTEMAS, type Params } from "@/lib/amortizacion";

const DEFAULTS: Params = { monto: 50000, tasaMensual: 3.3, cuotas: 6 };
const COLORES = ["var(--navy)", "var(--emerald)", "var(--chart-3)"];

export function Comparativa() {
  const [params, setParams] = useState<Params>(DEFAULTS);
  const errores = validar(params);
  const valido = Object.keys(errores).length === 0;

  const tablas = useMemo(
    () => (valido ? SISTEMAS.map((s) => ({ ...s, tabla: generarTabla(s.id, params) })) : []),
    [params, valido],
  );

  const dataBarras = tablas.map((t) => ({
    sistema: t.nombre.replace("Sistema ", ""),
    intereses: t.tabla.totalIntereses,
  }));
  const dataSaldo = valido
    ? Array.from({ length: params.cuotas + 1 }, (_, k) => {
        const row: Record<string, number> = { periodo: k };
        tablas.forEach((t) => {
          row[t.nombre.replace("Sistema ", "")] =
            k === 0 ? params.monto : (t.tabla.periodos[k - 1]?.saldoFinal ?? 0);
        });
        return row;
      })
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl">
          Comparativa de los 3 sistemas
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Mismos parámetros aplicados a los sistemas Francés, Alemán y Americano.
        </p>
      </div>
      <ParamsPanel params={params} onChange={setParams} errores={errores} />

      {!valido ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Corrige los parámetros para generar la comparativa.
        </p>
      ) : (
        <>
          <Card className="print-block overflow-hidden border-navy/15 p-0 shadow-sm">
            <div className="solo-movil space-y-3 p-4 lg:hidden">
              {tablas.map((t) => (
                <div key={t.id} className="rounded-lg border border-navy/15 p-4">
                  <p className="text-sm font-semibold text-navy">{t.nombre}</p>
                  <p className="text-xs text-muted-foreground">{t.desc}</p>
                  <div className="mt-2">
                    {[
                      { l: "Cuota período 1", v: fmtMXN(t.tabla.periodos[0]!.cuota) },
                      {
                        l: "Cuota último período",
                        v: fmtMXN(t.tabla.periodos[t.tabla.periodos.length - 1]!.cuota),
                      },
                      { l: "Total intereses", v: fmtMXN(t.tabla.totalIntereses) },
                      { l: "Total pagado", v: fmtMXN(t.tabla.totalPagado) },
                      { l: "Costo %", v: fmtPct(t.tabla.costoPct) },
                    ].map((x) => (
                      <div
                        key={x.l}
                        className="flex items-baseline justify-between gap-3 border-b border-dashed py-1.5 last:border-0"
                      >
                        <span className="text-xs text-muted-foreground">{x.l}</span>
                        <span className="num text-sm font-medium text-navy">{x.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="solo-desktop hidden overflow-x-auto lg:block">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="bg-navy text-primary-foreground">
                    <th className="px-3 py-2.5 text-left font-medium">Sistema</th>
                    <th className="px-3 py-2.5 text-right font-medium">Cuota período 1</th>
                    <th className="px-3 py-2.5 text-right font-medium">Cuota último período</th>
                    <th className="px-3 py-2.5 text-right font-medium">Total intereses</th>
                    <th className="px-3 py-2.5 text-right font-medium">Total pagado</th>
                    <th className="px-3 py-2.5 text-right font-medium">Costo %</th>
                  </tr>
                </thead>
                <tbody>
                  {tablas.map((t) => (
                    <tr key={t.id} className="border-b last:border-0 odd:bg-surface/60">
                      <td className="px-3 py-2.5 font-medium text-navy">
                        {t.nombre} <span className="text-xs text-muted-foreground">({t.desc})</span>
                      </td>
                      <td className="px-3 py-2.5 text-right num">
                        {fmtMXN(t.tabla.periodos[0]!.cuota)}
                      </td>
                      <td className="px-3 py-2.5 text-right num">
                        {fmtMXN(t.tabla.periodos[t.tabla.periodos.length - 1]!.cuota)}
                      </td>
                      <td className="px-3 py-2.5 text-right num text-chart-3">
                        {fmtMXN(t.tabla.totalIntereses)}
                      </td>
                      <td className="px-3 py-2.5 text-right num font-semibold">
                        {fmtMXN(t.tabla.totalPagado)}
                      </td>
                      <td className="px-3 py-2.5 text-right num text-emerald">
                        {fmtPct(t.tabla.costoPct)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="print-block border-navy/15 p-5 shadow-sm">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-navy">
                Total de intereses
              </h3>
              <div className="h-[260px] sm:h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dataBarras}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="sistema" tick={{ fontSize: 12 }} />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip formatter={(v: number) => fmtMXN(v)} />
                    <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 12 }} />
                    <Bar
                      dataKey="intereses"
                      name="Intereses"
                      fill="var(--navy)"
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card className="print-block border-navy/15 p-5 shadow-sm">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-navy">
                Evolución del saldo insoluto
              </h3>
              <div className="h-[260px] sm:h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dataSaldo}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip formatter={(v: number) => fmtMXN(v)} />
                    <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 12 }} />
                    {SISTEMAS.map((s, idx) => (
                      <Line
                        key={s.id}
                        type="monotone"
                        dataKey={s.nombre.replace("Sistema ", "")}
                        stroke={COLORES[idx]}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <Card className="print-block border-emerald/30 bg-emerald-soft p-5 shadow-sm">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-accent-foreground">
              Nota explicativa
            </h3>
            <p className="mt-2 text-sm text-accent-foreground/90">
              El sistema <strong>Alemán</strong> genera menos intereses porque amortiza capital más
              rápido: la cuota inicia alta y decrece período a período. El sistema{" "}
              <strong>Americano</strong> exige el pago más bajo por período (solo intereses), pero
              concentra todo el capital al final, por lo que el costo total de financiamiento es el
              más elevado. El <strong>Francés</strong> ofrece cuota fija, ideal para presupuestos
              estables, con un costo intermedio.
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
